<template>
  <div v-if="isAuthenticated" class="container">
    <ConversationList class="nav-bar" />
    <div class="chat-container">
      <AccountControls />
      <div class="conversation-container">
        <Conversation />
        <AccountControlPanels />
      </div>
    </div>
  </div>
  <LoadingIndicator v-else class="loading-spinner" />
  <FilesGlossaryDialogs />
  <AddSharedCollectionPopup
    :open="showSharedColPopup"
    :shared-link-id="shareCollection"
    :is-valid="validLink"
    :message="infoMessage"
    :collection="currentCollection"
    @close-popup="closeAddSharedCollectionPopup"
  />
</template>

<script>
import { onBeforeUnmount, onMounted, watch, ref, computed } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router/dist/vue-router';

import { Routes } from '~/constants/routes';
import { FetchUserData } from '~/constants/actions';

import useCollections from '~/composables/useCollections';
import usePlatformNotifications from '~/composables/usePlatformNotifications';
import useConversations from '~/composables/useConversations';
import useFormFilters from '~/composables/useFormFilters';
import useImageGeneration from '~/composables/useImageGeneration';

import Conversation from '~/components/conversation/Conversation';
import ConversationList from '~/components/conversation/ConversationList';
import LoadingIndicator from '~/components/common/LoadingIndicator';
import AccountControls from '~/components/account/AccountControls';
import AccountControlPanels from '~/components/account/AccountControlPanels';
import FilesGlossaryDialogs from '~/components/common/FilesGlossaryDialogs.vue';
import AddSharedCollectionPopup from '~/components/conversation/collections/AddSharedCollectionPopup';

export default {
  components: {
    AddSharedCollectionPopup,
    AccountControls,
    Conversation,
    ConversationList,
    LoadingIndicator,
    AccountControlPanels,
    FilesGlossaryDialogs,
  },
  setup() {
    const store = useStore();
    const { isAuthenticated } = useAuth0();
    const route = useRoute();
    const router = useRouter();
    const {
      currentConversation,
      isNewConversation,
      setCurrentConversation,
      setIsNewConversation,
      subscribeConversation,
      subscribeToConversationNamingChannel,
      unsubscribeConversation,
      unsubscribeFromConversationNamingChannel,
    } = useConversations();
    const { getBrandStyles, setUserImageForRemix } = useImageGeneration();
    const { getPlatformNotifications } = usePlatformNotifications();
    const { getUserCollections, validateSharedLink, openModalInCollectionScope } = useCollections();
    const { setInitialFilters } = useFormFilters();

    const currentRouteName = route.name;

    const currentRouteConversationId = ref(route.params.conversationId);
    const currentCollection = ref(null);
    const infoMessage = ref('');
    const validLink = ref(true);

    const conversationId = computed(() => currentConversation.value.conversationId);
    const shareCollection = computed(() => route.query.shareCollection);

    const showSharedColPopup = ref(false);

    const checkForCreateSharedColPopup = async () => {
      if (shareCollection.value) {
        const validation = await validateSharedLink(shareCollection.value);
        const { message, isValid = false, exist = false, collection = null } = validation || {};
        currentCollection.value = collection;
        infoMessage.value = message;
        validLink.value = isValid;
        if (exist) {
          await router.push({ path: route.path, query: {} });
          await openModalInCollectionScope(collection.collectionId, '');
        } else {
          showSharedColPopup.value = true;
        }
      }
    };
    const closeAddSharedCollectionPopup = () => {
      showSharedColPopup.value = false;
      router.push({ path: route.path, query: {} });
    };

    onMounted(async () => {
      store.dispatch(`authStore/${FetchUserData}`);
      getPlatformNotifications();
      subscribeToConversationNamingChannel();
      await getUserCollections();
      await checkForCreateSharedColPopup();
      await getBrandStyles();
    });

    // Unsubscribe from the Ably channel when the component is about to be destroyed
    onBeforeUnmount(() => {
      unsubscribeConversation(conversationId.value || currentRouteConversationId.value, currentRouteName);
      unsubscribeFromConversationNamingChannel();
      setInitialFilters();
      setUserImageForRemix(null);
    });

    watch(
      () => route.params.conversationId,
      async (newConversationId, oldConversationId) => {
        currentRouteConversationId.value = route.params.conversationId;

        // If navigating away from an existing conversation, unsubscribe from it
        if (oldConversationId) {
          unsubscribeConversation(oldConversationId, currentRouteName);
        }

        // If not creating a new conversation and a new ID exists, fetch and subscribe
        if (!isNewConversation.value && newConversationId) {
          await setCurrentConversation(newConversationId);
          subscribeConversation(newConversationId, Routes.Conversation.name);
        }

        setIsNewConversation(false);
      },
      { immediate: true, deep: true },
    );

    return {
      currentCollection,
      closeAddSharedCollectionPopup,
      isAuthenticated,
      infoMessage,
      shareCollection,
      showSharedColPopup,
      validLink,
    };
  },
};
</script>

<style scoped lang="scss">
.loading-spinner {
  width: 100vw;
  height: 100%;
}

.container {
  display: flex;
  background-color: $bb-neutral-0;
  height: 100%;
  width: 100%;
  flex-direction: row;

  .logo {
    margin-top: $bb-spacing-4;
    padding-left: $bb-spacing-4;
    align-self: flex-start;
    height: $bb-spacing-8;
    width: 140px;
  }

  .right-side-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 16px 16px 16px 0;
  }

  .chat-container {
    flex: auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .nav-bar {
    flex-shrink: 0;
    width: 100%;
  }
}

.conversation-container {
  display: flex;
  flex-direction: row;
  min-height: 0;
  flex: 1;
  width: 100%;
}
</style>
